declare global {
    interface Window {
        grecaptcha: {
            ready: (callback: () => void) => void;
            execute: (siteKey: string, options: { action: string }) => Promise<string>;
        };
    }
}

/**
 * Class responsible for handling Google Recaptcha v3 loading and form submission.
 */
export class RecaptchaFormHandler {
    private recaptchaLoaded: boolean = false;
    private readonly recaptchaPublicKey: string | null;
    private recaptchaLoadPromise: Promise<void> | null = null;

    constructor() {
        this.recaptchaPublicKey = this.getRecaptchaPublicKey();

        if (!this.recaptchaPublicKey) {
            console.error("Recaptcha public key not found in meta tags.");
            return;
        }

        this.recaptchaLoadPromise = this.loadRecaptcha();
        this.observeVisibility();
    }

    /**
     * Retrieves the Recaptcha public key from a meta tag.
     * @returns {string|null} The Recaptcha public key.
     */
    private getRecaptchaPublicKey(): string | null {
        const metaTag = document.querySelector('meta[name="recaptcha-public"]');
        return metaTag ? metaTag.getAttribute("content") : null;
    }

    /**
     * Loads the Recaptcha script and returns a Promise to handle race conditions.
     */
    private loadRecaptcha(): Promise<void> {
        return new Promise((resolve) => {
            // If already loaded globally, resolve immediately
            if (window.grecaptcha) {
                this.recaptchaLoaded = true;
                resolve();
                return;
            }

            const script = document.createElement("script");
            script.src = `https://www.google.com/recaptcha/api.js?render=${this.recaptchaPublicKey}`;

            script.onload = () => {
                this.recaptchaLoaded = true;
                window.grecaptcha.ready(() => {
                    this.refreshTokens();
                    resolve();
                });
            };

            script.onerror = () => {
                console.error("Failed to load Google reCAPTCHA script.");
            };

            document.head.appendChild(script);
        });
    }

    /**
     * Insert the reCaptcha token field to send with the form data.
     */
    private insertRecaptchaTokenField(form: HTMLFormElement): void {
        const formId = form.id;
        const action = form.getAttribute("data-recaptcha-action");

        if (!formId || !action) return;

        let tokenField = form.querySelector(`input[name="_recaptcha_token_${formId}"]`) as HTMLInputElement | null;

        if (!tokenField) {
            const tokenFieldId = `recaptcha_token_${formId}`;
            form.insertAdjacentHTML(
                "beforeend",
                `<input type="hidden" name="_recaptcha_token_${formId}" id="${tokenFieldId}">`
            );
            tokenField = form.querySelector(`#${tokenFieldId}`) as HTMLInputElement;
        }

        if (tokenField && tokenField.value && !tokenField.dataset.issuedAt) {
            tokenField.dataset.issuedAt = String(Math.floor(Date.now() / 1000));
        }
    }

    /**
     * Automatically refresh the form token every 90 seconds.
     */
    private refreshTokens(): void {
        setInterval(() => {
            if (!this.recaptchaLoaded || !window.grecaptcha) return;

            window.grecaptcha.ready(() => {
                document.querySelectorAll("[data-recaptcha-form]").forEach((el) => {
                    const form = el as HTMLFormElement;
                    const formId = form.id;
                    const action = form.getAttribute("data-recaptcha-action");
                    if (!action) return;

                    const tokenField = form.querySelector(`input[name="_recaptcha_token_${formId}"]`) as HTMLInputElement | null;

                    const issuedAt = Number(tokenField?.dataset.issuedAt || 0);
                    const age = Math.floor(Date.now() / 1000) - issuedAt;

                    // Only refresh if the token is getting old (> 90s)
                    if (tokenField && age > 90) {
                        window.grecaptcha.execute(this.recaptchaPublicKey!, {action})
                            .then((token: string) => {
                                tokenField.value = token;
                                tokenField.dataset.issuedAt = String(Math.floor(Date.now() / 1000));
                            })
                            .catch((error) => console.error("Error refreshing ReCaptcha token:", error));
                    }
                });
            });
        }, 90000);
    }

    /**
     * Sets up an Intersection Observer to monitor when forms are visible on the page.
     * Waits for the Recaptcha script to load before executing.
     */
    private observeVisibility(): void {
        const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;

                const form = entry.target as HTMLFormElement;
                this.insertRecaptchaTokenField(form);

                // Wait for the script to load before attempting to call grecaptcha.ready
                this.recaptchaLoadPromise?.then(() => {
                    window.grecaptcha.ready(() => {
                        const action = form.getAttribute("data-recaptcha-action");
                        if (action && this.recaptchaPublicKey) {
                            window.grecaptcha.execute(this.recaptchaPublicKey, {action}).then((token: string) => {
                                const tokenField = form.querySelector(`input[name="_recaptcha_token_${form.id}"]`) as HTMLInputElement | null;
                                if (tokenField) {
                                    tokenField.value = token;
                                    tokenField.dataset.issuedAt = String(Math.floor(Date.now() / 1000));
                                }
                            }).catch((error) => console.error("Error executing ReCaptcha:", error));
                        }
                    });
                });

                io.unobserve(form);
            });
        }, {threshold: 0.1});

        document.querySelectorAll("[data-recaptcha-form]").forEach((form) => {
            io.observe(form as HTMLFormElement);
        });
    }
}